import axios from "axios";

// =====================================================
// THEBLOGSSPHERE PRO — CLIENT API  (Task 6 / Task 7)
//
// One place for the Pro endpoints so the Pro Home, Create Content and My Pro
// Profile pages share the same base URL, the same auth header and the same
// limits instead of each re-declaring them (the boost pages follow the same
// pattern in boostApi.js).
// =====================================================

export const BASE_URL =
  typeof window !== "undefined" && window.location.hostname === "localhost"
    ? "http://localhost:5000"
    : "https://success-blog-app-backend.onrender.com";

// -----------------------------------------------------
// PRO CONTENT LIMITS
//
// These are what the composer enforces IN THE BROWSER, so a user is stopped
// before they upload rather than after. They are a convenience, not the rule:
// the server enforces the identical numbers on every write and is the only
// thing that decides whether a post is accepted.
// -----------------------------------------------------
export const PRO_LIMITS = {
  TITLE_MAX: 250,
  CONTENT_MAX: 2000,
  IMAGES_MAX: 10,
  VIDEO_SECONDS_MAX: 60 * 60, // 1 hour
};

// The free tier, shown next to the Pro numbers on the composer.
export const FREE_LIMITS = {
  TITLE_MAX: 100,
  CONTENT_MAX: 500,
  IMAGES_MAX: 1,
  VIDEO_SECONDS_MAX: 60,
};

function authConfig(extra = {}) {
  const token = localStorage.getItem("token");
  return {
    ...extra,
    headers: {
      ...(extra.headers || {}),
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
  };
}

// Membership state, straight from the server. Every Pro page asks this before
// it renders, so a lapsed or never-purchased account is sent back to the
// membership page instead of seeing a Pro screen it cannot use.
export async function getProStatus() {
  const res = await axios.get(`${BASE_URL}/api/pro-payment/status`, authConfig());
  return res.data;
}

// Everything the Pro Creator Profile shows: the existing account profile, the
// membership, the creator counters and every Pro post, newest first.
export async function getProProfile() {
  const res = await axios.get(`${BASE_URL}/api/pro/profile`, authConfig());
  return res.data;
}

// Publishes a Pro post. `formData` carries title, content, up to 10 images,
// one video and the post settings.
export async function createProContent(formData, onUploadProgress) {
  const res = await axios.post(
    `${BASE_URL}/api/pro/content`,
    formData,
    authConfig({
      headers: { "Content-Type": "multipart/form-data" },
      onUploadProgress,
    })
  );
  return res.data;
}

// Reads a video file's real duration in the browser, so an over-length video
// is refused before any of it is uploaded.
export function readVideoDuration(file) {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const el = document.createElement("video");

    el.preload = "metadata";

    el.onloadedmetadata = () => {
      const duration = el.duration;
      URL.revokeObjectURL(url);
      resolve(Number.isFinite(duration) ? duration : 0);
    };

    el.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error("Could not read this video file"));
    };

    el.src = url;
  });
}

export function formatDuration(seconds) {
  const total = Math.max(0, Math.round(Number(seconds) || 0));
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  const pad = (n) => String(n).padStart(2, "0");
  return h > 0 ? `${h}:${pad(m)}:${pad(s)}` : `${m}:${pad(s)}`;
}
